import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Grow,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { CONTAINMENT_COLORS } from '../../../../../configs/colors';
import { NoRecords } from '../../../../../Helpers/Common';
import { useMediaQuery } from '@mui/system';
import { NCRTooltip } from '../../../../Utilities/NCRFormHeading';
import { FirstLetterCaps } from '../../../../../Helpers/NCRHelpers';

const CustomBarTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '6px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #d1d5db',
        borderRadius: '4px'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 400 }}>{`${FirstLetterCaps(payload[0].payload.name)}`}</Typography>
      </div>
    );
  }
  return null;
};

export default function ContainmentChart({ containmentData }) {
  const isCustom = useMediaQuery('(min-width:1530px) and (max-width:1600px)')

  const sortedContainmentData = containmentData?.slice().sort((a, b) => {
    const countDiff = (b.count || 0) - (a.count || 0);
    if (countDiff !== 0) return countDiff;
    return (a.name || '').localeCompare((b.name || ''));
  }) || [];

  return (
    <Grow in={true} timeout={1700}>
      <Grid item xs={12} md={12} lg={6} xl={4}>
        <Grow
          in={true}
          style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
          timeout={3000}
        >
          <Card sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 380, display: 'flex', flexDirection: 'column', borderRadius: '2%' }}>
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', my: 1 }}>
                Yield Loss Analysis (Top 5)
              </Typography>
              {sortedContainmentData?.some(data => data.count > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={'70%'}>
                    <BarChart data={sortedContainmentData} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={false}
                        width={10}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomBarTooltip />} cursor={false} />
                      <Bar
                        dataKey="count"
                        radius={[0, 4, 4, 0]}
                        label={{ position: 'right', fontSize: 12, fill: '#000', fontWeight: 'normal' }}
                        isAnimationActive={false}
                        barSize={sortedContainmentData?.length >= 5 ? 30 : sortedContainmentData?.length === 4 ? 50 : sortedContainmentData?.length === 3 ? 60 : sortedContainmentData?.length === 2 ? 70 : 80}
                      >
                        {sortedContainmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CONTAINMENT_COLORS[index % CONTAINMENT_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: isCustom ? -1 : -3,
                    height: "30%"
                  }}>
                    <Typography variant="body2" sx={{ width: "20%", fontSize: "12px", textAlign: 'center', fontWeight: 400, cursor: 'default', userSelect: 'none' }}>
                      Count NCs
                    </Typography>
                    <Box sx={{ width: "80%", display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                      <Box sx={{ width: sortedContainmentData.slice(0, 3)?.some(item => item?.name?.includes('Concessionally accepted')) ? "50%" : sortedContainmentData?.length <= 3 ? '60%' : "35%", display: "grid", justifyContent: "flex-start", mt: 1, mb: 0.5 }}>
                        {sortedContainmentData.slice(0, 3)?.map((entry, index) => {
                          const displayValue = entry?.name?.length > 13 ? entry?.name.slice(0, 13) + "..." : entry?.name || "";
                          return (
                            <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: entry?.name?.length > 10 ? 'flex-start' : isCustom ? 'flex-start' : 'center', gap: 0.5 }}>
                              <Box sx={{ width: 12, height: 12, backgroundColor: CONTAINMENT_COLORS[index % CONTAINMENT_COLORS.length], borderRadius: '2px', flexShrink: 0, mt: entry?.name?.length > 10 ? 0.5 : 0 }} />
                              <NCRTooltip title={entry?.name?.length > 13 ? FirstLetterCaps(entry?.name) : ''}>
                                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', cursor: entry?.name?.length > 13 ? 'pointer' : 'default' }}>
                                  {displayValue && FirstLetterCaps(displayValue)}
                                </Typography>
                              </NCRTooltip>
                            </Box>
                          )
                        })}
                      </Box>
                      {sortedContainmentData?.length > 3 &&
                        <Box sx={{ width: sortedContainmentData.slice(3)?.some(item => item?.name?.includes('Concessionally accepted')) ? "50%" : "35%", display: "grid", justifyContent: "flex-start", mt: 1, mb: 0.5 }}>
                          {sortedContainmentData.slice(3)?.map((entry, index) => {
                            const displayValue = entry?.name?.length > 13 ? entry?.name.slice(0, 13) + "..." : entry?.name || "";
                            return (
                              <Box key={`legend-${index + 3}`} sx={{ display: 'flex', alignItems: entry?.name?.length > 10 ? 'flex-start' : isCustom ? 'flex-start' : 'center', gap: 0.5 }}>
                                <Box sx={{ width: 12, height: 12, backgroundColor: CONTAINMENT_COLORS[(index + 3) % CONTAINMENT_COLORS.length], borderRadius: '2px', flexShrink: 0, mt: entry?.name?.length > 10 ? 0.5 : 0 }} />
                                <NCRTooltip title={entry?.name?.length > 13 ? FirstLetterCaps(entry?.name) : ''}>
                                  <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', cursor: entry?.name?.length > 13 ? 'pointer' : 'default' }}>
                                    {displayValue && FirstLetterCaps(displayValue)}
                                  </Typography>
                                </NCRTooltip>
                              </Box>
                            )
                          })}
                        </Box>
                      }
                    </Box>
                  </Box>
                </>
              ) : (
                <NoRecords />
              )}
            </CardContent>
          </Card>
        </Grow>
      </Grid>
    </Grow>
  );
}
